import { Fragment, type ReactNode } from 'react'
import { childNodes, practicalContent, type LexicalNode } from '@/lib/practicalSection'
import { isCitationSup } from '@/lib/citations'
import { BOX_EYEBROW, TAIL_BOX } from './chip'

/**
 * Il riquadro delle «Informazioni pratiche», in coda all'articolo e FUORI DAL CORPO.
 *
 * PERCHÉ FUORI DAL `.prose` E NON UNO STILE DENTRO. La sezione pratica era invadente per una ragione
 * ereditaria, non per una scelta: dentro `.prose` prendeva `font-size: 1.125rem`, `line-height: 1.8`
 * e `--tw-prose-bullets: var(--color-accent)` (globals.css, 42-64, che sovrascrive Tailwind
 * Typography a mano), cioè veniva composta con la voce del racconto — corpo grande, interlinea da
 * lettura lunga, pallini colorati grandi come i titoletti — mentre è un riepilogo di dati che si
 * consulta, non si legge. Renderla fuori dal `.prose` fa cadere quell'eredità in blocco; il corpo
 * più piccolo qui è dichiarato a mano (`text-sm`) come fa <SourcesBlock>, non ottenuto per sottrazione.
 *
 * LA LINGUA DEL RIQUADRO è quella dell'aside TL;DR che stava in questa stessa colonna fino al commit
 * f405c95 (`rounded-card border border-accent/15 bg-accent-soft`, occhiello `text-xs font-semibold
 * uppercase tracking-widest text-accent-strong`): tinta ricavata dall'accento per-tenant, mai un
 * colore fisso, così ogni tenant ha il suo riquadro senza una riga di codice in più.
 *
 * E DEVE DIFFERENZIARSI DA <SourcesBlock>, che gli sta immediatamente sotto: quello è un filetto
 * grigio su fondo bianco con occhiello zinc-500, questo è un blocco pieno in tinta d'accento con
 * occhiello accent-strong. Senza quel differenziale la coda dell'articolo diventerebbe due riquadri
 * grigi uguali di cui il lettore non capisce la gerarchia.
 */
export default function PracticalBox({ nodes }: { nodes: LexicalNode[] }) {
  const { heading, items, notice, trailing } = practicalContent(nodes)

  // Una sezione col solo titolo non è un riquadro, è una cornice vuota: meglio non renderla.
  if (!items.length && !notice && !trailing.length) return null

  const hasData = items.length > 0 || trailing.length > 0

  return (
    // <aside> e non <section>: è il contenitore che il TL;DR usava in questa colonna, ed è la
    // definizione stessa dell'elemento (contenuto messo da parte rispetto al filo del testo).
    // NIENTE `id` + `aria-labelledby` alla <SourcesBlock>, e non è una dimenticanza: lo scroll
    // infinito monta N <ArticleView> nella stessa pagina, quindi un id fisso sarebbe duplicato N
    // volte e l'associazione andrebbe al primo articolo del DOM. L'<aside> sta dentro il suo
    // <article>, il suo <h2> lo nomina nell'outline: l'associazione c'è senza id.
    // `aria-label` e non `aria-labelledby`: senza un nome accessibile un <aside> dentro un <article>
    // NON mappa su `complementary` (ARIA in HTML: la mappatura vale solo fuori da
    // article/aside/main/nav/section, oppure quando l'elemento ha un nome), quindi il ruolo sarebbe
    // `generic` e «contenuto messo da parte» non arriverebbe a chi usa uno screen reader. Il nome si
    // dà con l'attributo e non con un id perché lo scroll infinito monta N <ArticleView> nella stessa
    // pagina: un id fisso sarebbe duplicato N volte e l'associazione andrebbe al primo del DOM (è il
    // difetto che <SourcesBlock> ha oggi con `id="fonti"`). `useId()` non è la via d'uscita: questo
    // componente è renderizzato anche come Server Component, dove gli hook non sono ammessi.
    // `TAIL_BOX`: il vestito è lo stesso di <AuthorCard>, che chiude la coda come questo la apre.
    // Il margine non sta qui — lo tiene il contenitore in <ArticleView>.
    <aside aria-label={heading} className={TAIL_BOX}>
      {/* IL TITOLO SI MOSTRA, ma con la tipografia dell'occhiello. Si mostra perché è l'unica cosa
          che dice al lettore cos'è il riquadro, e perché toglierlo aprirebbe un buco nell'outline
          dei titoli che l'articolo aveva quando la sezione stava nel corpo (h2 nel sommario, nei
          lettori di schermo e negli anchor di Google). Resta un <h2> per quello; è piccolo e in
          maiuscoletto per non competere con i titoli di sezione del corpo, che sono grandi e neri —
          la stessa scelta che <SourcesBlock> fa sul suo «Fonti». Il testo è quello che l'articolo
          ha scritto, non la costante: sul post 43 dice «Indirizzo, proprietà e contatti». */}
      <h2 className={BOX_EYEBROW}>{heading}</h2>

      {items.length ? (
        // Un <ul> e non un <dl>: la coppia etichetta/valore chiederebbe <dt>/<dd>, ma l'etichetta
        // NON è garantita su ogni voce (la riga di accessibilità non ne ha) e un <dd> senza <dt> non
        // è conforme. <SourcesBlock> ha già fissato il pattern ul + li per questa famiglia.
        // Niente pallini: Preflight azzera `list-style`, e qui è quello che vogliamo — il pallino
        // colorato grande è metà del problema che ha aperto la richiesta. `role="list"` esplicito
        // proprio per quello: con `list-style: none` Safari/VoiceOver toglie all'elenco il ruolo di
        // lista, e chi ascolta perde il «5 elementi» che dice che questa è una scheda di dati.
        <ul role="list" className="mt-3 space-y-2 leading-relaxed text-zinc-600">
          {items.map((item, i) => (
            <li key={i}>
              {/* I due punti li stampa il riquadro, non l'articolo: nel post 58 stanno dentro il
                  grassetto («Come arrivare:»), nel post 43 aprono il valore («: Strada…»). Le due
                  convenzioni sono normalizzate a monte (practicalItem) e ricomposte qui in una sola,
                  altrimenti la stessa sezione si leggerebbe in due modi su due articoli.
                  Lo spazio è esplicito: uno spazio a fine riga JSX viene mangiato. */}
              {/* L'etichetta è l'unica cosa in `text-ink`: nel post 58 il valore contiene altri
                  grassetti («1,2 KM», «2 euro») e con lo stesso trattamento le due cose diventavano
                  indistinguibili — «Come arrivare:» e «1,2 KM» stampati identici. Qui l'etichetta è
                  più scura, l'enfasi dentro il valore resta del colore del valore. */}
              {item.label ? (
                <>
                  <strong className="font-semibold text-ink">{item.label}:</strong>{' '}
                </>
              ) : null}
              {/* Una voce che dichiara di NON sapere non pesa come una che dice un orario: sul post
                  43 due voci su sei sono la formula di assenza, e stampate col peso delle altre si
                  mangiano un terzo del riquadro senza dare un'informazione. Non si cancellano (è
                  testo pubblicato, e un riquadro che nasconde una riga dell'articolo è un riquadro di
                  cui non ci si fida): scendono al corpo dell'avviso, che è il registro giusto — anche
                  loro dicono «verificare prima di partire». */}
              <span className={item.isAbsence ? 'text-xs text-zinc-500' : undefined}>
                <Inline nodes={item.value} keyBase={`i${i}`} />
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {/* L'AVVISO DI ACCESSIBILITÀ HA UN TRATTAMENTO SUO, staccato da un filetto e in corpo minore,
          perché non è un dato come gli altri: è una cosa che diciamo noi al lettore («nessuno è
          andato a controllare»), non un valore letto da una fonte. Messa in fila con orari e
          biglietto sembrava l'informazione mancante di un modulo, ed è la riga che l'utente ha
          indicato come la più invadente. Qui non porta l'etichetta in grassetto — sarebbe la sesta
          voce dell'elenco — e non scende sotto zinc-600: è un avviso di accessibilità, sarebbe
          grottesco renderlo il testo meno leggibile della pagina (zinc-500 su fondo in tinta sfiora
          il 4.5:1 di AA a 12px, zinc-600 sta largo). Il filetto usa il bordo del riquadro, non un
          grigio: dentro un blocco in tinta un border-zinc-100 si legge come una crepa. */}
      {notice ? (
        <p
          className={`${
            hasData ? 'mt-4 border-t border-accent/15 pt-3' : 'mt-3'
          } text-xs leading-relaxed text-zinc-600`}
        >
          {notice.label ? `${notice.label}: ` : ''}
          <Inline nodes={notice.value} keyBase="a" />
        </p>
      ) : null}

      {/* I BLOCCHI DI CODA VANNO DOPO L'AVVISO, e l'ordine non è estetico: sull'unica istanza reale
          quel paragrafo PARLA dell'avviso. Post 43: «Sull'accessibilità la scheda comunale riporta la
          formula standard "non presenta barriere architettoniche", senza alcun dettaglio sul percorso
          […] per questo QUI NON VIENE RIPETUTO come se lo fosse.» Messo prima, «qui non viene
          ripetuto» si legge prima della riga a cui si riferisce, e la spiegazione arriva senza il suo
          oggetto. Nell'articolo l'ordine è elenco (accessibilità ultima delle sei voci) → paragrafo:
          tenendo l'avviso davanti alla coda il riquadro conserva quell'ordine. */}
      {trailing.length ? (
        <div className="mt-3 space-y-2 leading-relaxed text-zinc-600">
          {trailing.map((block, i) => (
            <p key={i}>
              <Inline nodes={childNodes(block)} keyBase={`t${i}`} />
            </p>
          ))}
        </div>
      ) : null}
    </aside>
  )
}

/**
 * I nodi inline di una voce. NON si appiattisce a testo semplice: nei valori del post 58 «1,2 KM» e
 * «2 euro» sono nodi `text` in grassetto DENTRO il valore, e sono l'unica gerarchia che il valore ha
 * (l'etichetta è un'altra cosa: è il primo figlio, e la riconosce practicalItem).
 *
 * Dei formati di Lexical se ne trattano tre: grassetto (1), corsivo (2) e APICE DI CITAZIONE (64).
 * Un formato non trattato degrada a testo semplice: perde l'enfasi, non le parole — e le parole sono
 * il punto.
 *
 * L'APICE NON POTEVA DEGRADARE, ed è la ragione per cui questo componente compare in una modifica
 * che sembra riguardare solo il corpo. Il testo salvato di una citazione è `[3]`, e sui pezzi
 * generati quando gli apici esistevano è ancora lì (post 62): senza il caso esplicito qui la voce
 * «Costo biglietto: intero 10 euro» uscirebbe come «intero 10 euro[3]» stampato a misura piena in
 * mezzo alla scheda dati — una cifra appiccicata a un prezzo, proprio nel punto della pagina in cui
 * i numeri contano di più. Ora l'apice non si rende affatto: da dove viene il prezzo lo dice
 * l'elenco delle fonti in coda (<SourcesBlock>), che è tutto ciò che questa pagina promette.
 *
 * Sui link NON si aggiunge `nofollow`, a differenza di <SourcesBlock>: là le fonti sono link in
 * uscita che dichiariamo tali, qui è testo dell'articolo che cambia solo tipografia. Il converter di
 * default non mette nessun rel, e un riquadro non deve cambiare la semantica SEO dei link del corpo:
 * `target`/`rel` seguono `fields.newTab` come fa lui.
 */
function Inline({ nodes, keyBase }: { nodes: LexicalNode[]; keyBase: string }) {
  return <>{nodes.map((node, i) => renderInline(node, `${keyBase}-${i}`))}</>
}

function renderInline(node: LexicalNode, key: string): ReactNode {
  if (typeof node?.text === 'string') {
    const format = Number(node.format) || 0
    if (isCitationSup(format, node.text)) return null
    let out: ReactNode = node.text
    if (format & 2) out = <em>{out}</em>
    // Grassetto senza `text-ink`: dentro il riquadro il near-black è riservato all'etichetta della
    // voce (vedi sopra), qui l'enfasi resta del colore del testo che la contiene.
    if (format & 1) out = <strong className="font-semibold">{out}</strong>
    return <Fragment key={key}>{out}</Fragment>
  }

  if (node?.type === 'linebreak') return <br key={key} />

  if (node?.type === 'link' || node?.type === 'autolink') {
    const fields = node.fields as { url?: string; newTab?: boolean } | undefined
    const url = typeof fields?.url === 'string' ? fields.url : ''
    const inner = <Inline nodes={childNodes(node)} keyBase={key} />
    if (!url) return <Fragment key={key}>{inner}</Fragment>
    return (
      <a
        key={key}
        href={url}
        target={fields?.newTab ? '_blank' : undefined}
        rel={fields?.newTab ? 'noopener noreferrer' : undefined}
        className="font-medium underline decoration-zinc-300 underline-offset-2 transition-colors hover:text-accent-strong"
      >
        {inner}
      </a>
    )
  }

  // Qualunque altro nodo: si scende nei figli. Un tipo che oggi non prevediamo perde il suo
  // involucro, non il suo testo.
  return <Inline key={key} nodes={childNodes(node)} keyBase={key} />
}
